import { apiSlice } from './apiSlice';



export interface ChangePlatformPasswordParams {
  oldPassword: string;
  newPassword: string;
}

export interface PlatformManagementQueryParams {
  skip?: number;
  take?: number;
  search?: string;
  status?: string;
}

export interface PlatformPayload {
  name: string;
  currency: string;
  balanceUrl: string;
  betUrl: string;
  resultUrl: string;
  rollbackUrl: string;
  // PREPAID (default): platform funds a wallet up front — send ggrPercent + assignedCredit.
  // POSTPAID: no wallet, billed manually later — send postpaidRatePercent instead.
  // Send exactly the pair matching billingMode; the backend rejects the other mode's fields.
  billingMode?: 'PREPAID' | 'POSTPAID';
  ggrPercent?: number;
  assignedCredit?: number;
  postpaidRatePercent?: number;
  // Which vendor(s)/provider(s) to grant this platform on creation — omit to default to all of
  // GAP (see PlatformsService.create() / PlatformProviderAccessService).
  providerSelections?: { vendorCode: string; allProviders?: boolean; providers?: string[] }[];
  password: string;
}

export interface EditPlatformPayload {
  platformId: string;
  name: string;
  currency: string;
  balanceUrl: string;
  betUrl: string;
  resultUrl: string;
  rollbackUrl: string;
  status: string;
  ggrPercent: number;
}

export interface ManageBalancePayload {
  platformId: string;
  amount: number;
  remark: string;
}

export interface UpdatePlatformSubProviderStatusPayload {
  platformId: string;
  subproviderId: string;
  status: string;
  updateInPlatform: boolean;
  // Required by the backend — sub-provider names are not unique across vendors, so a specific
  // vendor must always be named (see CasinoGameService.requireVendorId).
  vendorCode: string;
}

export interface UpdatePlatformProviderStatusPayload {
  platformId: string;
  providerId: string;
  status: string;
  updateInPlatform: boolean;
  // Required by the backend — provider names are not unique across vendors, so a specific
  // vendor must always be named (see CasinoGameService.requireVendorId).
  vendorCode: string;
}

export interface UpdatePlatformGameStatusPayload {
  platformId: string;
  gameId: number;
  status: 'ACTIVE' | 'INACTIVE';
  updateInPlatform?: boolean;
}

export interface UpdatePlatformGameStatusResponse {
  success: boolean;
  message: string;
}

export interface UpdatePlatformProviderStatusResponse {
  success: boolean;
  message: string;
}

export interface PlatformManagement {
  id: string;
  name: string;
  currency: string;
  balanceUrl: string;
  betUrl: string;
  resultUrl: string;
  rollbackUrl: string;
  status: string;
  createdAt: Date;
  updatedAt: Date;
  billingMode?: 'PREPAID' | 'POSTPAID';
  postpaidRatePercent?: number | null;
  casinoWallets: {
    id: number;
    ggrPercent: number;
    assignedCredit: number;
    createdAt: string;
    updatedAt: string;
    platformId: string;
  }[];
}

export interface PlatformResponse {
  count: number;
  skip: number;
  take: number;
  data: PlatformManagement[];
}

export interface AddPlatformResponse {
  id: string;
  name: string;
  currency: string;
  balanceUrl: string;
  betUrl: string;
  resultUrl: string;
  rollbackUrl: string;
  status: string;
  createdAt: string;
  updatedAt: string;
  billingMode: 'PREPAID' | 'POSTPAID';
  postpaidRatePercent: number | null;
  casinoWallets: {
    id: number;
    ggrPercent: number;
    assignedCredit: number;
    createdAt: string;
    updatedAt: string;
    platformId: string;
  }[];
  meta: {
    passwordSalt: string;
    passwordHash: string;
    platformId: string;
  };
}

export interface ManageBalanceResponse {
  id: string;
  platformId: string;
  type: 'DEPOSIT' | 'WITHDRAWAL' | string;
  amount: number;
  casinoPoints: number;
  createdAt: string;
  updatedAt: string;
  remark: string;
}

export interface PlatformGame {
  id: number;
  name: string;
  category: string;
  provider: string;
  subProvider: string;
  urlThumb: string;
  gameCode: string;
  rtp: number;
  status: string;
  platformGames: {
    status: string;
  }[];
  platformStatus: string;
}

export interface PlatformGamesResponse {
  count: number;
  data: PlatformGame[];
  total?: number;
}

export interface PlatformList {
  id: string;
  name: string;
  billingMode?: 'PREPAID' | 'POSTPAID';
}

export const platformApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    changePlatformPassword: builder.mutation<{ status: string }, ChangePlatformPasswordParams>({
      query: (credentials) => ({
        url: `/platforms/me/change-password`,
        method: 'POST',
        body: credentials,
      }),
      invalidatesTags: ['Platforms'],
    }),
    getPlatforms: builder.query<PlatformResponse, PlatformManagementQueryParams>({
      query: ({ skip, take, search, status }) => ({
        url: '/platforms',
        params: {
          ...(skip !== undefined && { skip }),
          ...(take !== undefined && { take }),
          ...(search && { search }),
          ...(status && { status }),
          _t: Date.now(),
        },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      }),
      providesTags: ['Platforms'],
    }),

    getPlatformById: builder.query<PlatformManagement, string>({
      query: (platformId) => ({
        url: `/platforms/${platformId}`,
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      }),
      providesTags: ['Platforms'],
    }),

    // Self-service: a platform's own basic details (billingMode etc.) off its own JWT — never
    // takes an id, so there's nothing for a platform to substitute to see another platform's.
    getMyPlatform: builder.query<PlatformManagement, void>({
      query: () => ({
        url: '/platforms/me',
        params: { _t: Date.now() },
        headers: {
          'Cache-Control': 'no-cache',
          Pragma: 'no-cache',
        },
      }),
      providesTags: ['Platforms'],
    }),

    getPlatformList: builder.query<PlatformList[], void>({
      query: () => ({
        url: '/platforms/all',
      }),
      providesTags: ['Platforms'],
    }),

    updatePlatformStatus: builder.mutation<
      { status: string; message: string },
      { platformId: string; status: 'Active' | 'Inactive' }
    >({
      query: ({ platformId, status }) => ({
        url: `/platforms/${platformId}/${status}`,
        method: 'POST',
        body: { status },
      }),
      invalidatesTags: ['Platforms'],
    }),

    AddPlatform: builder.mutation<AddPlatformResponse, PlatformPayload>({
      query: (body) => ({
        url: `/platforms`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Platforms'],
    }),

    EditPlatform: builder.mutation<{ status: string }, EditPlatformPayload>({
      query: ({
        name,
        balanceUrl,
        betUrl,
        resultUrl,
        rollbackUrl,
        ggrPercent,
        currency,
        platformId,
      }) => ({
        url: `/platforms/me?platformId=${platformId}`,
        method: 'PATCH',
        body: {
          name,
          balanceUrl,
          betUrl,
          resultUrl,
          rollbackUrl,
          ggrPercent,
          currency,
        },
      }),
      invalidatesTags: ['Platforms'],
    }),

    AddBalance: builder.mutation<ManageBalanceResponse, ManageBalancePayload>({
      query: (body) => ({
        url: `/platform-transaction/add-balance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Platforms'],
    }),

    SubtractBalance: builder.mutation<ManageBalanceResponse, ManageBalancePayload>({
      query: (body) => ({
        url: `/platform-transaction/subtract-balance`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Platforms'],
    }),

    updatePlatformGameStatus: builder.mutation<
      UpdatePlatformGameStatusResponse,
      UpdatePlatformGameStatusPayload
    >({
      query: ({ platformId, gameId, status, updateInPlatform }) => ({
        url: `/casino-games/platform/${platformId}/game/${gameId}/status`,
        method: 'PATCH',
        body: { status, updateInPlatform },
      }),
      invalidatesTags: ['Platforms'],
    }),

    updatePlatformProviderStatus: builder.mutation<
      UpdatePlatformProviderStatusResponse,
      UpdatePlatformProviderStatusPayload
    >({
      query: ({ platformId, providerId, status, updateInPlatform, vendorCode }) => ({
        url: `/casino-games/platform/${platformId}/provider/${providerId}/status`,
        method: 'PATCH',
        body: { status, updateInPlatform, vendorCode },
      }),
      invalidatesTags: ['Platforms'],
    }),
    updatePlatformSubProviderStatus: builder.mutation<
      UpdatePlatformProviderStatusResponse,
      UpdatePlatformSubProviderStatusPayload
    >({
      query: ({ platformId, subproviderId, status, updateInPlatform, vendorCode }) => ({
        url: `/casino-games/platform/${platformId}/subProvider/${subproviderId}/status`,
        method: 'PATCH',
        body: { status, updateInPlatform, vendorCode },
      }),
      invalidatesTags: ['Platforms'],
    }),

    getPlatformGames: builder.query<PlatformGamesResponse, { platformId: string }>({
      query: ({ platformId }) => ({
        url: `/casino-games/platform/${platformId}/games`,
        method: 'GET',
        params: { _t: Date.now() },
      }),
      providesTags: ['Platforms'],
    }),
  }),
});

export const {
  useGetPlatformsQuery,
  useLazyGetPlatformsQuery,
  useGetPlatformByIdQuery,
  useGetMyPlatformQuery,
  useGetPlatformListQuery,
  useUpdatePlatformStatusMutation,
  useAddPlatformMutation,
  useEditPlatformMutation,
  useAddBalanceMutation,
  useSubtractBalanceMutation,
  useUpdatePlatformGameStatusMutation,
  useUpdatePlatformProviderStatusMutation,
  useGetPlatformGamesQuery,
  useLazyGetPlatformGamesQuery,
  useUpdatePlatformSubProviderStatusMutation,
  useChangePlatformPasswordMutation,
} = platformApi;
